/**
 * A NOTA DA RAÇÃO — o preço de uma compra, lançado depois dela.
 *
 * Pedido do gestor em 25/09/2026. A ração chega no barracão e o tratador lança
 * o que chegou; o preço fica para o escritório, a qualquer tempo, porque a nota
 * fiscal de ração não é conta para se fazer debaixo do sol:
 *
 *   «vem com valor total, depois vem os descontos, depois vem o valor do
 *    frete, e quando a carga tem mais de um tipo de ração nela…»
 *
 * Esta é a conta que o escritório faz: divide o desconto e o frete da nota
 * entre as compras que ela cobre, e chega ao custo de cada uma.
 *
 * ── DESCONTO E FRETE, POR UNIDADE ─────────────────────────────────────────
 *
 * Decisão do gestor: os dois se dividem pela QUANTIDADE, na unidade em que a
 * nota vem — «se tem 740 volumes na entrega e o desconto ficou em R$ 3.330, o
 * desconto será aplicado ou via sacos ou via kg, depende do como estiver no
 * cadastro a NF-e». Por saco ou por quilo dá o mesmo resultado quando todos os
 * sacos da nota pesam igual, que é o caso comum; difere quando 25 kg e 30 kg
 * vêm juntos, e aí manda a unidade da nota.
 *
 * Nota em SACOS só vale quando todas as compras dela foram lançadas em saco,
 * com o peso do saco daquela compra. Compra lançada em quilo não sabe quantos
 * sacos eram — e dividir o quilo por um peso de saco de outra compra seria
 * inventar sacos.
 *
 * ── A CONTA FECHA AO CENTAVO ──────────────────────────────────────────────
 *
 * Em centavos inteiros, nunca em número quebrado: 0,1 + 0,2 não dá 0,3 em
 * ponto flutuante, e uma nota de 38 mil reais somada item a item sairia com um
 * centavo de diferença do papel. O resto da divisão vai para o item de maior
 * quantidade — onde um centavo pesa menos —, desempatado pelo identificador,
 * para a mesma nota dar sempre a mesma conta. A soma dos custos de cada compra
 * bate EXATAMENTE com «itens − desconto + frete»: é a prova que o escritório
 * confere contra a nota impressa, a mesma disciplina do romaneio.
 */

export type UnidadeDaNota = 'saco' | 'kg'

export interface ItemParaRatear {
  /** A compra — linha de `mov_racao`. */
  id: string
  /** O valor deste item na nota, antes de desconto e frete. */
  valor_brl: number
  quantidade_kg: number
  /** O peso do saco DESTA compra. Nulo quando ela foi lançada em quilo. */
  peso_saco_kg: number | null
}

export interface NotaParaRatear {
  unidade: UnidadeDaNota
  desconto_brl: number
  frete_brl: number
  itens: ItemParaRatear[]
}

export interface ItemRateado {
  id: string
  /** Na unidade da nota — sacos ou quilos. */
  quantidade: number
  valor_brl: number
  desconto_brl: number
  frete_brl: number
  /** O que esta compra custou de verdade: valor − desconto + frete. */
  custo_brl: number
  preco_kg_brl: number
  /** Nulo quando a compra não foi lançada em saco. */
  preco_saco_brl: number | null
}

export interface NotaRateada {
  itens: ItemRateado[]
  total_itens_brl: number
  total_brl: number
}

export type SemRateio =
  | 'sem_itens'
  | 'valor_invalido'
  | 'quantidade_zero'
  | 'saco_sem_peso'
  | 'desconto_maior'
  | 'custo_negativo'

const centavos = (brl: number): number => Math.round(brl * 100)

function quantidadeNaUnidade(i: ItemParaRatear, unidade: UnidadeDaNota): number | null {
  if (unidade === 'kg') return i.quantidade_kg
  if (i.peso_saco_kg === null || !(i.peso_saco_kg > 0)) return null
  return i.quantidade_kg / i.peso_saco_kg
}

/**
 * Divide `total` centavos proporcionalmente a `pesos`, com o resto no maior
 * peso (desempatado pela ordem de `ids`). A soma devolvida é exatamente `total`.
 */
function repartir(total: number, pesos: number[], ids: string[]): number[] {
  const soma = pesos.reduce((t, p) => t + p, 0)
  if (total === 0 || soma <= 0) return pesos.map(() => 0)
  const partes = pesos.map((p) => Math.floor((total * p) / soma))
  const resto = total - partes.reduce((t, x) => t + x, 0)
  let maior = 0
  for (let k = 1; k < pesos.length; k += 1) {
    const pk = pesos[k] ?? 0
    const pm = pesos[maior] ?? 0
    if (pk > pm || (pk === pm && (ids[k] ?? '') < (ids[maior] ?? ''))) maior = k
  }
  partes[maior] = (partes[maior] ?? 0) + resto
  return partes
}

/** Por que a nota não se rateia — para a tela dizer, em vez de a conta sumir. */
export function porQueNaoRateia(n: NotaParaRatear): SemRateio | null {
  if (n.itens.length === 0) return 'sem_itens'
  const numeros = [n.desconto_brl, n.frete_brl, ...n.itens.map((i) => i.valor_brl)]
  if (numeros.some((x) => !Number.isFinite(x) || x < 0)) return 'valor_invalido'
  if (n.itens.some((i) => !(i.quantidade_kg > 0))) return 'quantidade_zero'
  if (n.itens.some((i) => quantidadeNaUnidade(i, n.unidade) === null)) return 'saco_sem_peso'
  const itens = n.itens.reduce((t, i) => t + centavos(i.valor_brl), 0)
  if (centavos(n.desconto_brl) > itens) return 'desconto_maior'
  const r = ratearSemConferir(n)
  if (r.itens.some((i) => i.custo_brl < 0)) return 'custo_negativo'
  return null
}

function ratearSemConferir(n: NotaParaRatear): NotaRateada {
  const ids = n.itens.map((i) => i.id)
  const qtd = n.itens.map((i) => quantidadeNaUnidade(i, n.unidade) ?? 0)
  const descontos = repartir(centavos(n.desconto_brl), qtd, ids)
  const fretes = repartir(centavos(n.frete_brl), qtd, ids)

  const itens = n.itens.map((i, k) => {
    const valor = centavos(i.valor_brl)
    const custo = valor - (descontos[k] ?? 0) + (fretes[k] ?? 0)
    const custoBrl = custo / 100
    return {
      id: i.id,
      quantidade: qtd[k] ?? 0,
      valor_brl: valor / 100,
      desconto_brl: (descontos[k] ?? 0) / 100,
      frete_brl: (fretes[k] ?? 0) / 100,
      custo_brl: custoBrl,
      preco_kg_brl: custoBrl / i.quantidade_kg,
      preco_saco_brl:
        i.peso_saco_kg !== null && i.peso_saco_kg > 0
          ? custoBrl / (i.quantidade_kg / i.peso_saco_kg)
          : null,
    }
  })

  const totalItens = n.itens.reduce((t, i) => t + centavos(i.valor_brl), 0)
  return {
    itens,
    total_itens_brl: totalItens / 100,
    total_brl: (totalItens - centavos(n.desconto_brl) + centavos(n.frete_brl)) / 100,
  }
}

/** O rateio, ou nulo quando a nota não fecha — ver `porQueNaoRateia`. */
export function ratearNota(n: NotaParaRatear): NotaRateada | null {
  return porQueNaoRateia(n) === null ? ratearSemConferir(n) : null
}

/**
 * O CUSTO DE CADA COMPRA, a partir das notas válidas — uma fonte só.
 *
 * O servidor chama isto uma vez por pedido e carimba o resultado nas pilhas e
 * na lista de movimentos. Duas leituras separadas do preço são o jeito
 * clássico de ganhar duas telas discordando.
 */
export function custosDasCompras(
  notas: NotaParaRatear[],
): Map<string, { custo_brl: number; preco_kg_brl: number; preco_saco_brl: number | null }> {
  const mapa = new Map<string, { custo_brl: number; preco_kg_brl: number; preco_saco_brl: number | null }>()
  for (const n of notas) {
    const r = ratearNota(n)
    // Nota que não fecha não dá preço a ninguém — o servidor não a teria
    // aceitado, e se ela existe assim, a compra aparece «sem preço», que é a
    // verdade sobre ela, e não com um número inventado.
    if (r === null) continue
    for (const i of r.itens) {
      mapa.set(i.id, { custo_brl: i.custo_brl, preco_kg_brl: i.preco_kg_brl, preco_saco_brl: i.preco_saco_brl })
    }
  }
  return mapa
}
